import {
  createContact,
  createContacts,
  createGroup,
  deleteContacts,
  deleteGroups,
  getContacts,
  getGroups,
  ContactsBulkUpdateParams,
  updateMultipleContacts,
  deleteAllContacts,
} from 'lib/api';
import { CONTACTS_ACTIONS, CreateParams, QueryParams } from '.';
import {
  GetGroupParams,
  UpdateContactParams,
  UpdateGroupParams,
} from './types';
import { getGroup, getOptOuts, updateGroup } from '../api/contactsApi';

const {
  GET_CONTACTS_START,
  GET_CONTACTS_SUCCESS,
  GET_CONTACTS_ERROR,
  ADD_CONTACT_START,
  ADD_CONTACT_SUCCESS,
  ADD_CONTACTS_SUCCESS,
  BULK_CONTACT_UPDATE_START,
  BULK_CONTACT_UPDATE_SUCCESS,
  BULK_CONTACT_UPDATE_ERROR,
  DELETE_CONTACTS_START,
  DELETE_CONTACTS_ERROR,
  DELETE_CONTACTS_SUCCESS,
  ADD_CONTACT_ERROR,
  GET_GROUPS_START,
  GET_GROUPS_SUCCESS,
  GET_GROUPS_ERROR,
  GET_GROUP_START,
  GET_GROUP_SUCCESS,
  GET_GROUP_ERROR,
  GET_GROUP_CONTACTS_START,
  GET_GROUP_CONTACTS_SUCCESS,
  GET_GROUP_CONTACTS_ERROR,
  ADD_GROUP_START,
  ADD_GROUP_SUCCESS,
  ADD_GROUP_ERROR,
  DELETE_GROUPS_START,
  DELETE_GROUPS_SUCCESS,
  DELETE_GROUPS_ERROR,
  EDIT_GROUP_START,
  EDIT_GROUP_SUCCESS,
  EDIT_GROUP_ERROR,
  GET_OPT_OUTS_START,
  GET_OPT_OUTS_SUCCESS,
  GET_OPT_OUTS_ERROR,
} = CONTACTS_ACTIONS;

export const loadContacts = async ({
  search,
  page,
  size,
  sort,
  dispatch,
  conversation,
}: QueryParams): Promise<void> => {
  dispatch({ type: GET_CONTACTS_START });

  const contactsData = await getContacts({
    search,
    page,
    size,
    sort,
    conversation,
  }).catch(err => err);
  if (contactsData instanceof Error) {
    dispatch({
      type: GET_CONTACTS_ERROR,
      error: contactsData.message,
    });
    return;
  }
  const { contacts, count } = contactsData;
  const payload = {
    count,
    contacts,
    page,
    size,
    search,
  };
  dispatch({ type: GET_CONTACTS_SUCCESS, payload });
};

export const loadGroupContacts = async ({
  groupId,
  search,
  page,
  size,
  sort,
  dispatch,
}: QueryParams): Promise<void> => {
  dispatch({ type: GET_GROUP_CONTACTS_START });

  const contactsData = await getContacts({
    groupId,
    search,
    page,
    size,
    sort,
  }).catch(err => err);
  if (contactsData instanceof Error) {
    dispatch({
      type: GET_GROUP_CONTACTS_ERROR,
      error: contactsData.message,
    });
    return;
  }
  const { contacts, count } = contactsData;
  const payload = {
    count,
    contacts,
    page,
    size,
    search,
  };
  dispatch({ type: GET_GROUP_CONTACTS_SUCCESS, payload });
};

export const addContact = async ({
  data,
  dispatch,
}: CreateParams): Promise<void> => {
  dispatch({ type: ADD_CONTACT_START });

  const contact = await createContact({ data }).catch(err => err);
  if (contact instanceof Error) {
    dispatch({
      type: ADD_CONTACT_ERROR,
      error: contact.message,
    });
    return;
  }
  const payload = {
    contact,
  };
  dispatch({ type: ADD_CONTACT_SUCCESS, payload });
};

export const updateContactsBulk = async (params: UpdateContactParams) => {
  const { data, dispatch } = params;
  dispatch({ type: BULK_CONTACT_UPDATE_START });
  const updated = await updateMultipleContacts(
    data as ContactsBulkUpdateParams
  ).catch(err => err);
  if (updated instanceof Error) {
    dispatch({
      type: BULK_CONTACT_UPDATE_ERROR,
      error: updated.message,
    });
    return;
  }

  const payload = {
    contacts: updated.contacts,
    groups: updated.groups,
  };
  dispatch({ type: BULK_CONTACT_UPDATE_SUCCESS, payload });
};

export const addContacts = async ({
  data,
  dispatch,
}: CreateParams): Promise<void> => {
  dispatch({ type: ADD_CONTACT_START });

  const { contacts, count } = await createContacts({ data }).catch(err => err);
  if (contacts instanceof Error) {
    dispatch({
      type: ADD_CONTACT_ERROR,
      error: contacts.message,
    });
    return;
  }
  const payload = {
    contacts,
    count,
  };
  dispatch({ type: ADD_CONTACTS_SUCCESS, payload });
};

export const removeContacts = async ({
  data,
  dispatch,
}: CreateParams): Promise<void> => {
  dispatch({ type: DELETE_CONTACTS_START });

  const contacts = await deleteContacts({ data }).catch(err => err);
  if (contacts instanceof Error) {
    dispatch({
      type: DELETE_CONTACTS_ERROR,
      error: contacts.message,
    });
    return;
  }
  const payload = {
    contacts,
    data,
  };
  dispatch({ type: DELETE_CONTACTS_SUCCESS, payload });
};

export const removeAllContacts = async ({
  data,
  dispatch,
}: CreateParams): Promise<void> => {
  dispatch({ type: DELETE_CONTACTS_START });

  const contacts = await deleteAllContacts({ data }).catch(err => err);
  if (contacts instanceof Error) {
    dispatch({
      type: DELETE_CONTACTS_ERROR,
      error: contacts.message,
    });
    return;
  }
  const payload = {
    contacts,
    data,
  };
  dispatch({ type: DELETE_CONTACTS_SUCCESS, payload });
};

export const removeGroups = async ({
  data,
  dispatch,
}: CreateParams): Promise<void> => {
  dispatch({ type: DELETE_GROUPS_START });

  const groups = await deleteGroups({ data }).catch(err => err);
  if (groups instanceof Error) {
    dispatch({
      type: DELETE_GROUPS_ERROR,
      error: groups.message,
    });
    return;
  }
  const payload = {
    groups,
    data,
  };
  dispatch({ type: DELETE_GROUPS_SUCCESS, payload });
};

export const loadGroups = async ({
  search,
  page = 0,
  size = 10,
  sort,
  order = '',
  dispatch,
}: QueryParams): Promise<void> => {
  dispatch({ type: GET_GROUPS_START });

  const groupsData = await getGroups({ search, page, size, sort, order }).catch(
    err => err
  );
  if (groupsData instanceof Error) {
    dispatch({
      type: GET_GROUPS_ERROR,
      error: groupsData.message,
    });
    return;
  }
  const { groups, count } = groupsData;
  const payload = {
    count,
    groups,
    page,
    size,
    search,
  };
  dispatch({ type: GET_GROUPS_SUCCESS, payload });
};

export const loadGroup = async ({
  groupId,
  dispatch,
}: GetGroupParams): Promise<void> => {
  dispatch({ type: GET_GROUP_START });

  const groupsData = await getGroup({ groupId }).catch(err => err);
  if (groupsData instanceof Error) {
    dispatch({
      type: GET_GROUP_ERROR,
      error: groupsData.message,
    });
    return;
  }
  const { group } = groupsData;
  const payload = {
    group,
  };
  dispatch({ type: GET_GROUP_SUCCESS, payload });
};

export const addGroup = async ({ data, dispatch }: CreateParams) => {
  dispatch({ type: ADD_GROUP_START });

  const group = await createGroup({ data }).catch(err => err);
  if (group instanceof Error) {
    dispatch({
      type: ADD_GROUP_ERROR,
      error: group.message,
    });
    return;
  }
  const payload = {
    group,
  };
  dispatch({ type: ADD_GROUP_SUCCESS, payload });
  return group;
};

export const editGroup = async ({
  data,
  dispatch,
}: UpdateGroupParams): Promise<void> => {
  dispatch({ type: EDIT_GROUP_START });

  const group = await updateGroup(data).catch(err => err);
  if (group instanceof Error) {
    dispatch({
      type: EDIT_GROUP_ERROR,
      error: group.message,
    });
    return;
  }
  const payload = {
    group,
  };
  dispatch({ type: EDIT_GROUP_SUCCESS, payload });
};

export const loadOptOuts = async ({
  search,
  page,
  size,
  sort,
  dispatch,
}: QueryParams): Promise<void> => {
  dispatch({ type: GET_OPT_OUTS_START });

  const optOutsData = await getOptOuts({ search, page, size, sort }).catch(
    err => err
  );
  if (optOutsData instanceof Error) {
    dispatch({
      type: GET_OPT_OUTS_ERROR,
      error: optOutsData.message,
    });
    return;
  }
  const { optOuts, count } = optOutsData;
  const payload = {
    count,
    optOuts,
    page,
    size,
    search,
  };
  dispatch({ type: GET_OPT_OUTS_SUCCESS, payload });
};
